import React, { useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import Modal from "../../../components/Modal";
import DataTable from "../../../components/DataTable";
import EmptyValue from "../../../components/EmptyValue";
import useReceivePurchaseOrderLines from "../../hooks/useReceivePurchaseOrderLines";

// Receive-from-shortages (§Phase-7-UI, 2026-09-06) — reachable from a Requested/Delayed
// row's Action column on all three procurement views, so a pharmacist looking at a
// medicine (not a purchase order) can record a delivery without first navigating to
// /v2/inventory/procurement/purchase-orders to find the right one.
//
// Reuses useReceivePurchaseOrderLines — the SAME hook and endpoint the PO detail page
// uses (PATCH /api/inventory/orders/:id/receive), not a second receive path. Per-line
// "Receive quantity" is a DELTA added to the backend's running total, not a new
// absolute total — matching the PO detail page's semantics and copy exactly (this
// modal's markup is a deliberate, close port of that page's line table).
//
// A medicine's open lines (row.openOrderLines) can span more than one purchase order —
// different vendors, different placedAt — so submitting groups the entered lines by
// inventoryOrderId and issues ONE PATCH PER order, never a single combined call.
function ReceiveModal({ row, onClose }) {
    const receiveLines = useReceivePurchaseOrderLines();
    const [drafts, setDrafts] = useState({}); // keyed by inventoryOrderItemId — the entered delta
    const [savingLineId, setSavingLineId] = useState(null);
    const [isSavingAll, setIsSavingAll] = useState(false);

    const lines = row.openOrderLines || [];

    const setDraft = (lineId, value) => setDrafts((prev) => ({ ...prev, [lineId]: value }));

    const fillRemaining = (line) => setDraft(line.inventoryOrderItemId, String(line.quantityOrdered - line.quantityReceived));

    const submitLines = async (linesToSubmit) => {
        const byOrderId = {};
        linesToSubmit.forEach((line) => {
            if (!byOrderId[line.inventoryOrderId]) byOrderId[line.inventoryOrderId] = [];
            byOrderId[line.inventoryOrderId].push({
                inventoryOrderItemId: line.inventoryOrderItemId,
                quantityReceived: parseInt(drafts[line.inventoryOrderItemId], 10),
            });
        });

        try {
            await Promise.all(
                Object.entries(byOrderId).map(([orderId, patchLines]) =>
                    receiveLines.mutateAsync({ orderId: parseInt(orderId, 10), lines: patchLines })
                )
            );
            toast.success("Receipt recorded");
            setDrafts((prev) => {
                const next = { ...prev };
                linesToSubmit.forEach((line) => delete next[line.inventoryOrderItemId]);
                return next;
            });
            return true;
        } catch (error) {
            toast.error(error?.err || "Error recording receipt");
            return false;
        }
    };

    const receiveLine = async (line) => {
        const quantity = parseInt(drafts[line.inventoryOrderItemId], 10);
        if (!quantity || quantity <= 0) {
            toast.warn("Enter a quantity received first");
            return;
        }
        setSavingLineId(line.inventoryOrderItemId);
        const ok = await submitLines([line]);
        setSavingLineId(null);
        if (ok && lines.length === 1) onClose();
    };

    const receiveAllEntered = async () => {
        const linesToSubmit = lines.filter((line) => parseInt(drafts[line.inventoryOrderItemId], 10) > 0);
        if (linesToSubmit.length === 0) {
            toast.warn("Enter a quantity received for at least one line");
            return;
        }
        setIsSavingAll(true);
        const ok = await submitLines(linesToSubmit);
        setIsSavingAll(false);
        if (ok) onClose();
    };

    const columns = [
        { key: "vendor", label: "Vendor", render: (line) => line.vendorName || <EmptyValue /> },
        { key: "quantityOrdered", label: "Ordered" },
        { key: "quantityReceived", label: "Received" },
        {
            key: "remaining",
            label: "Remaining",
            render: (line) => {
                const remaining = line.quantityOrdered - line.quantityReceived;
                return <strong className={remaining > 0 ? "text-[var(--ops-amber-700)]" : "text-[var(--ops-carbon-400)]"}>{remaining}</strong>;
            },
        },
        {
            key: "receiveQuantity",
            label: "Receive quantity",
            render: (line) => {
                const remaining = line.quantityOrdered - line.quantityReceived;
                if (remaining <= 0) return <EmptyValue />;
                return (
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min="1"
                            max={remaining}
                            className="w-20 h-9 border border-[var(--ops-carbon-200)] rounded-lg px-2 text-sm"
                            value={drafts[line.inventoryOrderItemId] || ""}
                            onChange={(event) => setDraft(line.inventoryOrderItemId, event.target.value)}
                            placeholder="0"
                        />
                        <button
                            type="button"
                            className="text-xs font-semibold text-[var(--ops-emerald-600)] hover:underline"
                            onClick={() => fillRemaining(line)}
                        >
                            Fill remaining
                        </button>
                    </div>
                );
            },
        },
        {
            key: "action",
            label: "Action",
            render: (line) => {
                const remaining = line.quantityOrdered - line.quantityReceived;
                if (remaining <= 0) return null;
                return (
                    <button
                        type="button"
                        className="px-3 py-1.5 rounded-lg bg-[var(--ops-emerald-100)] text-[var(--ops-emerald-600)] border border-[var(--ops-emerald-200)] text-xs font-semibold disabled:opacity-50"
                        onClick={() => receiveLine(line)}
                        disabled={savingLineId === line.inventoryOrderItemId}
                    >
                        {savingLineId === line.inventoryOrderItemId ? "Saving…" : "Receive"}
                    </button>
                );
            },
        },
    ];

    return (
        <Modal
            title={`Receive stock — ${row.medicineName || "Medicine"}`}
            onClose={onClose}
            // Wider than Modal's default (max-w-lg sm:max-w-2xl md:max-w-3xl) — six
            // columns (Vendor/Ordered/Received/Remaining/Receive quantity/Action) were
            // clipping the vendor name and the Receive button at the default width.
            size="max-w-lg sm:max-w-3xl md:max-w-5xl"
            footer={
                <>
                    <button
                        type="button"
                        className="px-4 py-2 rounded-lg border border-[var(--ops-carbon-200)] text-sm font-semibold"
                        onClick={onClose}
                    >
                        Close
                    </button>
                    <button
                        type="button"
                        onClick={receiveAllEntered}
                        disabled={isSavingAll || lines.length === 0}
                        className="bg-elm-500 text-white-50 px-4 py-2 rounded-lg font-sans font-semibold disabled:opacity-50"
                    >
                        {isSavingAll ? "Saving…" : "Receive all entered"}
                    </button>
                </>
            }
        >
            {lines.length === 0 ? (
                <p className="text-sm text-[var(--ops-carbon-500)]">No open purchase-order lines for this medicine.</p>
            ) : (
                <DataTable columns={columns} rows={lines} />
            )}
        </Modal>
    );
}

ReceiveModal.propTypes = {
    row: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default ReceiveModal;
